// 勤務特典の階層定義
export type WorkRewardTier = {
  minHours: number;
  maxHours: number;
  reward: number;
};

// 直前キャンセル1件あたりのペナルティ（時間）
export const PENALTY_HOURS_PER_LATE_CANCELLATION = 10;

/** JST でこの日付以降に 2026年5月分の特典テーブルを適用 */
export const WORK_REWARD_POLICY_EFFECTIVE_JST_DATE = "2026-05-01";

/** JST でこの日付以降に 2026年10月分の特典テーブルを適用 */
export const WORK_REWARD_POLICY_2026_10_EFFECTIVE_JST_DATE = "2026-10-01";

// ペナルティ時間を計算（直前キャンセル数 × 10時間）
export const calculatePenaltyHours = (
  lateCancellationCount: number,
): number => {
  return lateCancellationCount * PENALTY_HOURS_PER_LATE_CANCELLATION;
};

// 特典計算用の有効勤務時間を計算（実際の勤務時間 - ペナルティ、0未満にはならない）
export const calculateEffectiveWorkHours = (
  actualHours: number,
  penaltyHours: number,
): number => {
  return Math.max(0, actualHours - penaltyHours);
};

/** 2026-05-01（JST）未満で適用する従来テーブル */
export const WORK_REWARD_TIERS_LEGACY: WorkRewardTier[] = [
  { minHours: 5, maxHours: 10, reward: 500 },
  { minHours: 10, maxHours: 30, reward: 1000 },
  { minHours: 30, maxHours: 40, reward: 2000 },
  { minHours: 40, maxHours: 60, reward: 3000 },
  { minHours: 60, maxHours: 100, reward: 4000 },
  { minHours: 100, maxHours: Infinity, reward: 5000 },
];

/** 2026年5月分（JST 2026-05-01 以降） */
export const WORK_REWARD_TIERS_2026_05: WorkRewardTier[] = [
  { minHours: 5, maxHours: 10, reward: 500 },
  { minHours: 10, maxHours: 30, reward: 1000 },
  { minHours: 30, maxHours: 40, reward: 1500 },
  { minHours: 40, maxHours: 60, reward: 2000 },
  { minHours: 60, maxHours: 100, reward: 2500 },
  { minHours: 100, maxHours: Infinity, reward: 3000 },
];

/** 2026年10月分（JST 2026-10-01 以降）。care_link_server Constants::WorkReward::TIERS_2026_10 と同一 */
export const WORK_REWARD_TIERS_2026_10: WorkRewardTier[] = [
  { minHours: 5, maxHours: 10, reward: 300 },
  { minHours: 10, maxHours: 30, reward: 500 },
  { minHours: 30, maxHours: 40, reward: 800 },
  { minHours: 40, maxHours: 60, reward: 1000 },
  { minHours: 60, maxHours: 100, reward: 1500 },
  { minHours: 100, maxHours: Infinity, reward: 2000 },
];

/** `Date` 時点の JST 暦日を `YYYY-MM-DD` で返す */
export const getJstDateKey = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  if (!y || !m || !d) {
    throw new Error("getJstDateKey: missing date parts");
  }
  return `${y}-${m}-${d}`;
};

export const selectWorkRewardTiers = (
  date: Date = new Date(),
): WorkRewardTier[] => {
  const jstDateKey = getJstDateKey(date);
  if (jstDateKey >= WORK_REWARD_POLICY_2026_10_EFFECTIVE_JST_DATE) {
    return WORK_REWARD_TIERS_2026_10;
  }
  if (jstDateKey >= WORK_REWARD_POLICY_EFFECTIVE_JST_DATE) {
    return WORK_REWARD_TIERS_2026_05;
  }
  return WORK_REWARD_TIERS_LEGACY;
};

/** 表示用に現在適用すべきテーブル（`selectWorkRewardTiers` と同じ） */
export const getWorkRewardTiersForDisplay = selectWorkRewardTiers;

const giftAmountJa = (reward: number): string => reward.toLocaleString("ja-JP");

/** 今月勤務特典ダイアログ・マイページ訴求共通の Amber 系カラー */
export const WORK_REWARD_DIALOG_COLORS = {
  headerBg: "#FEF3E2",
  activeCellBg: "#FAEEDA",
  activeCellBorder: "#EF9F27",
  headerIcon: "#BA7517",
  headerLabel: "#854F0B",
} as const;

/** 適用中テーブルの最大特典額（例: 2,000） */
export const getMaxWorkRewardAmount = (date: Date = new Date()): number => {
  const tiers = selectWorkRewardTiers(date);
  return Math.max(...tiers.map((tier) => tier.reward));
};

/** 初回勤務完了ダイアログ等の見出し用（例: Amazonギフト券 2,000円分） */
export const formatMaxWorkRewardSummary = (date: Date = new Date()): string => {
  const maxAmount = getMaxWorkRewardAmount(date);
  return `Amazonギフト券 ${giftAmountJa(maxAmount)}円分`;
};

/** 初回勤務完了ダイアログのプロモーション行（例: 継続勤務で最大 Amazonギフト券 2,000円分プレゼント） */
export const formatMaxWorkRewardPromotionLine = (
  date: Date = new Date(),
): string => `継続勤務で最大 ${formatMaxWorkRewardSummary(date)}プレゼント`;

/** 初回勤務完了ダイアログの特典カード見出し */
export const formatMaxWorkRewardCardHeader = (): string =>
  "継続勤務でAmazonギフト券プレゼント";

/** 初回勤務完了ダイアログの特典カード金額（例: 2,000） */
export const formatMaxWorkRewardAmountValue = (
  date: Date = new Date(),
): string => giftAmountJa(getMaxWorkRewardAmount(date));

// 現在の勤務時間に基づく特典階層を取得
export const getCurrentRewardTier = (
  hours: number,
  date: Date = new Date(),
): WorkRewardTier | undefined => {
  if (hours < 5) return undefined;
  const tiers = selectWorkRewardTiers(date);
  return tiers.find((tier) => hours >= tier.minHours && hours < tier.maxHours);
};

// 次の特典階層を取得
export const getNextRewardTier = (
  hours: number,
  date: Date = new Date(),
): WorkRewardTier | undefined => {
  const tiers = selectWorkRewardTiers(date);
  return tiers.find((tier) => hours < tier.minHours);
};

/** マイページカード用（例: Amazonギフト券1,000円分） */
export const formatAmazonGiftSummary = (
  tier: WorkRewardTier | undefined,
): string => {
  if (!tier) return "";
  return `Amazonギフト券${giftAmountJa(tier.reward)}円分`;
};

/** モーダル内の金額行用（例: 1,000円分） */
export const formatAmazonGiftAmountLine = (
  tier: WorkRewardTier | undefined,
): string => {
  if (!tier) return "";
  return `${giftAmountJa(tier.reward)}円分`;
};

/** フッター等の短い表記（例: アマギフ1,000円分） */
export const formatAmazonGiftShortLabel = (tier: WorkRewardTier): string => {
  return `アマギフ${giftAmountJa(tier.reward)}円分`;
};

// プログレスバーの値を計算（最大100hを100%とする）
export const calculateWorkProgress = (hours: number): number => {
  const maxHours = 100;
  return Math.min((hours / maxHours) * 100, 100);
};

// グラデーションテキストスタイル
export const gradientTextStyle = {
  teal: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  blue: {
    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  indigo: {
    background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
} as const;
